import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const DimSum: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Classic Dim Sum Recipes | Chinese Food Maps</title>
        <meta name="description" content="Learn how to make authentic dim sum at home. Our guide includes recipes for classic har gow (shrimp dumplings), siu mai, and other dim sum favorites." />
        <meta property="og:title" content="Classic Dim Sum Recipes | Chinese Food Maps" />
        <meta property="og:description" content="Learn how to make authentic dim sum at home." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/dim-sum" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/dim-sum" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Classic Dim Sum Recipes</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Dim Sum (点心) literally means "touch the heart" and refers to a style of Chinese cuisine prepared as small bite-sized portions traditionally served in small steamer baskets or on small plates.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mb-6">Har Gow (Crystal Shrimp Dumplings)</h2>
            
            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Wrapper:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>1 cup wheat starch</li>
              <li>¼ cup tapioca starch</li>
              <li>1 cup boiling water</li>
              <li>1 tsp vegetable oil</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Filling:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>½ lb shrimp, chopped</li>
              <li>¼ cup bamboo shoots, minced</li>
              <li>1 tsp ginger, grated</li>
              <li>1 tsp sesame oil</li>
              <li>½ tsp white pepper</li>
              <li>½ tsp salt</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Make the Dough:</strong> Mix starches, add boiling water and oil. Knead until smooth.
              </li>
              <li>
                <strong>Prepare Filling:</strong> Mix all filling ingredients well.
              </li>
              <li>
                <strong>Form Dumplings:</strong> Roll dough pieces into thin circles, fill, and pleat edges.
              </li>
              <li>
                <strong>Steam:</strong> Steam for 6-7 minutes until translucent.
              </li>
            </ol>

            <h2 className="text-2xl font-bold text-gray-800 mb-6 mt-12">Siu Mai (Open-Top Pork and Shrimp Dumplings)</h2>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Ingredients:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>½ lb ground pork</li>
              <li>¼ lb shrimp, chopped</li>
              <li>2 shiitake mushrooms, minced</li>
              <li>2 water chestnuts, minced</li>
              <li>1 tbsp soy sauce</li>
              <li>1 tsp sesame oil</li>
              <li>20 wonton wrappers</li>
              <li>Carrot for garnish</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Mix Filling:</strong> Combine all ingredients except wrappers and carrot.
              </li>
              <li>
                <strong>Form Dumplings:</strong> Place filling in center of wrappers, shape into cylinders.
              </li>
              <li>
                <strong>Garnish:</strong> Top each with grated carrot.
              </li>
              <li>
                <strong>Steam:</strong> Steam for 8-10 minutes until cooked through.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Keep wrappers covered while working to prevent drying</li>
                <li>Don't overfill the dumplings</li>
                <li>Line steamer baskets with parchment paper</li>
                <li>Serve immediately while hot</li>
                <li>Prepare dipping sauces: soy sauce, chili oil, or black vinegar</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};